import Entity from 'src/app/core/domain/entity';
import UniqueEntityID from 'src/app/core/domain/unique_entity_id';

export enum NotificationType {
  PatientCreated = 'patient_created',
  PatientMsdpCreated = 'patient_msdp_created',
  AppointmentMsdpCreated = 'appointment_msdp_created',
  AppointmentMsdpUpdated = 'appointment_msdp_updated',
  AppointmentMsdpDeleted = 'appointment_msdp_deleted',
  PatientBirthday = 'patient_birthday',
  SurveyUpdated = 'survey_updated',
  NewSponsorship = 'new_sponsorship',
  NEW_DIET_INVITATION = 'new_diet_invitation',
  NEW_DIET_MESSAGE = 'new_diet_message',
  NEW_SHARED_DOC = 'new_shared_doc',
  NEW_ENCOURAGEMENT = 'new_encouragement',
  NEXT_APPOINTMENT_UPDATED = 'next_appointment_updated',
  DIARY_COMMENT = 'diary_comment',
  TEST = 'test',
}

export interface NotificationProps {
  createdAt: Date;
  type: NotificationType;
  readAt?: Date;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload?: any;
}

export class NotificationId extends Entity<unknown> {
  private constructor(id?: UniqueEntityID) {
    super(null, id);
  }

  public static create(id?: UniqueEntityID): NotificationId {
    return new NotificationId(id);
  }
}

export class Notification extends Entity<NotificationProps> {
  get notificationId(): NotificationId {
    return NotificationId.create(this.id);
  }

  get type(): NotificationType {
    return this.props.type;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get payload(): any | undefined {
    return this.props.payload;
  }

  get readAt(): Date | undefined {
    return this.props.readAt;
  }

  get createdAt(): Date {
    return this.props.createdAt;
  }

  get title(): string {
    switch (this.type) {
      case NotificationType.PatientCreated:
      case NotificationType.PatientMsdpCreated:
        return 'Nouveau patient';
      case NotificationType.AppointmentMsdpCreated:
        return 'Rendez-vous Patient ajouté';
      case NotificationType.AppointmentMsdpUpdated:
        return 'Rendez-vous Patient modifié';
      case NotificationType.AppointmentMsdpDeleted:
        return 'Rendez-vous Patient supprimé';
      case NotificationType.PatientBirthday:
        return 'Anniversaire';
      case NotificationType.SurveyUpdated:
        return 'Réponses questionnaire';
      case NotificationType.NewSponsorship:
        return 'Parrainage';
      default:
        return '--';
    }
  }

  get message(): string {
    switch (this.type) {
      case NotificationType.PatientCreated:
        return `Un nouveau patient ${
          this.props.payload?.patientFullName
            ? '"' + this.props.payload?.patientFullName + '" '
            : ''
        }vient d'être rattaché à votre compte.`;
      case NotificationType.PatientMsdpCreated:
        return `Un nouveau patient ${
          this.props.payload?.patientFullName
            ? '"' + this.props.payload?.patientFullName + '" '
            : ''
        }vient d'être créé depuis votre compte MonSuiviDiet Plus.`;
      case NotificationType.AppointmentMsdpCreated: {
        const appointmentDate: Date = new Date(this.props.payload?.date);
        const date =
          (appointmentDate.getDate() < 10
            ? '0' + appointmentDate.getDate()
            : appointmentDate.getDate()) +
          '/' +
          (appointmentDate.getMonth() + 1 < 10
            ? '0' + (appointmentDate.getMonth() + 1)
            : appointmentDate.getMonth() + 1) +
          '/' +
          appointmentDate.getFullYear();
        const time =
          appointmentDate.getHours() +
          ':' +
          (appointmentDate.getMinutes() < 10
            ? '0' + appointmentDate.getMinutes()
            : appointmentDate.getMinutes());
        return `Un nouveau rendez-vous le ${date} à ${time} ${
          this.props.payload?.patientFullName
            ? 'avec "' + this.props.payload?.patientFullName + '" '
            : ''
        }vient d'être ajouté depuis MonSuiviDiet Plus.`;
      }
      case NotificationType.AppointmentMsdpUpdated: {
        const appointmentDate: Date = new Date(this.props.payload?.date);
        const date =
          (appointmentDate.getDate() < 10
            ? '0' + appointmentDate.getDate()
            : appointmentDate.getDate()) +
          '/' +
          (appointmentDate.getMonth() + 1 < 10
            ? '0' + (appointmentDate.getMonth() + 1)
            : appointmentDate.getMonth() + 1) +
          '/' +
          appointmentDate.getFullYear();
        const time =
          appointmentDate.getHours() +
          ':' +
          (appointmentDate.getMinutes() < 10
            ? '0' + appointmentDate.getMinutes()
            : appointmentDate.getMinutes());
        const oldAppointmentDate: Date = new Date(this.props.payload?.oldDate);
        const oldDate =
          (oldAppointmentDate.getDate() < 10
            ? '0' + oldAppointmentDate.getDate()
            : oldAppointmentDate.getDate()) +
          '/' +
          (oldAppointmentDate.getMonth() + 1 < 10
            ? '0' + (oldAppointmentDate.getMonth() + 1)
            : oldAppointmentDate.getMonth() + 1) +
          '/' +
          oldAppointmentDate.getFullYear();
        const oldTime =
          oldAppointmentDate.getHours() +
          ':' +
          (oldAppointmentDate.getMinutes() < 10
            ? '0' + oldAppointmentDate.getMinutes()
            : oldAppointmentDate.getMinutes());

        return `Le rendez-vous du ${oldDate} à ${oldTime} ${
          this.props.payload?.patientFullName
            ? 'avec "' + this.props.payload?.patientFullName + '" '
            : ''
        }vient d'être déplacé au ${date} à ${time} depuis MonSuiviDiet Plus.`;
      }
      case NotificationType.AppointmentMsdpDeleted: {
        const appointmentDate: Date = new Date(this.props.payload?.date);
        const date =
          (appointmentDate.getDate() < 10
            ? '0' + appointmentDate.getDate()
            : appointmentDate.getDate()) +
          '/' +
          (appointmentDate.getMonth() + 1 < 10
            ? '0' + (appointmentDate.getMonth() + 1)
            : appointmentDate.getMonth() + 1) +
          '/' +
          appointmentDate.getFullYear();
        const time =
          appointmentDate.getHours() +
          ':' +
          (appointmentDate.getMinutes() < 10
            ? '0' + appointmentDate.getMinutes()
            : appointmentDate.getMinutes());
        return `Le rendez-vous du ${date} à ${time} ${
          this.props.payload?.patientFullName
            ? 'avec "' + this.props.payload?.patientFullName + '" '
            : ''
        }vient d'être annulé depuis MonSuiviDiet Plus.`;
      }
      case NotificationType.PatientBirthday:
        return `Le patient ${
          this.props.payload?.patientFullName
            ? '"' + this.props.payload?.patientFullName + '" '
            : ''
        }fête en ce jour, son anniversaire.`;
      case NotificationType.SurveyUpdated:
        return `Le patient ${
          this.props.payload?.patientFullName
            ? '"' + this.props.payload?.patientFullName + '" '
            : ''
        }a répondu au questionnaire ${this.props.payload?.surveyName}.`;
      case NotificationType.NewSponsorship:
        return `${this.props.payload?.dietitianFullName} a utilisé votre code de parrainage.`;
      default:
        return '--';
    }
  }

  get isActionAvailable(): boolean {
    switch (this.type) {
      case NotificationType.PatientCreated:
        return !!this.payload['userId'];
      default:
        return false;
    }
  }

  private constructor(props: NotificationProps, id?: UniqueEntityID) {
    super(props, id);
  }

  copyWith(props: NotificationProps): Notification {
    return Notification.create(
      {
        ...this.props,
        ...props,
      },
      this.id,
    );
  }

  public static create(
    props: NotificationProps,
    id?: UniqueEntityID,
  ): Notification {
    return new Notification(props, id);
  }
}
